import { useMemo, useState } from 'react';
import MandatoryDisclosureReview from './MandatoryDisclosureReview';
import PagesReview from './PagesReview';
import { usePlatformApiReadiness } from './api/platformApi';

type SiteKey = 'warren' | 'turtles';

type NavItem = {
  label: string;
  badge?: string;
};

type NavGroup = {
  label: string;
  items: NavItem[];
};

const sites: Record<SiteKey, { name: string; domain: string; mark: string }> = {
  warren: {
    name: 'Warren Academy',
    domain: 'warrenacademy.net',
    mark: 'WA',
  },
  turtles: {
    name: 'Turtles',
    domain: 'turtles.school',
    mark: 'T',
  },
};

const navigationBySite: Record<SiteKey, NavGroup[]> = {
  warren: [
    {
      label: 'Content',
      items: [
        { label: 'Pages' },
        { label: 'Blog' },
        { label: 'News' },
        { label: 'Notices' },
        { label: 'Events' },
        { label: 'Achievements' },
      ],
    },
    {
      label: 'School',
      items: [
        { label: 'Transfer Certificates' },
        { label: 'Mandatory Disclosure' },
        { label: 'Documents' },
      ],
    },
    {
      label: 'Experience',
      items: [
        { label: 'Photo Galleries' },
        { label: 'Warren Pulse', badge: 'Live' },
        { label: 'Navigation' },
        { label: 'SEO & Redirects' },
      ],
    },
  ],
  turtles: [
    {
      label: 'Content',
      items: [
        { label: 'Pages' },
        { label: 'Blog' },
        { label: 'Events' },
        { label: 'Bulletin' },
        { label: 'Photo Galleries' },
      ],
    },
    {
      label: 'Turtles',
      items: [
        { label: 'Programs' },
        { label: 'Branches' },
        { label: 'Parent Stories' },
        { label: 'Documents' },
      ],
    },
    {
      label: 'Experience',
      items: [
        { label: "What's Happening", badge: 'Live' },
        { label: 'Navigation' },
        { label: 'SEO & Redirects' },
      ],
    },
  ],
};

const recentItems = [
  { type: 'Blog', title: 'Building confident learners', status: 'Draft', time: '12 min ago' },
  { type: 'Notice', title: 'Parent interaction schedule', status: 'Published', time: '48 min ago' },
  { type: 'Gallery', title: 'Campus life — September', status: 'Review', time: '2 hr ago' },
  { type: 'Page', title: 'Admissions overview', status: 'Published', time: 'Yesterday' },
];

const metrics = [
  { label: 'Published content', value: '128', detail: '+6 this week' },
  { label: 'Scheduled items', value: '07', detail: 'Next at 08:00' },
  { label: 'Media assets', value: '3,842', detail: 'Optimisation ready' },
  { label: 'Redirects', value: '214', detail: 'Legacy URLs protected' },
];

function App() {
  const [siteKey, setSiteKey] = useState<SiteKey>('warren');
  const [activeItem, setActiveItem] = useState('Dashboard');
  const { status: apiStatus, retry: retryApi } = usePlatformApiReadiness();
  const site = sites[siteKey];
  const navGroups = navigationBySite[siteKey];

  const accentClass = useMemo(() => `admin-shell site-${siteKey}`, [siteKey]);
  const isReadOnlyReview = activeItem === 'Pages' || (siteKey === 'warren' && activeItem === 'Mandatory Disclosure');

  const switchSite = (nextSite: SiteKey) => {
    setSiteKey(nextSite);
    setActiveItem('Dashboard');
  };

  return (
    <div className={accentClass}>
      {apiStatus.phase !== 'ready' && (
        <div className="api-connection-gate" role="status" aria-live="polite">
          <div className="api-connection-card">
            <span
              className={`api-connection-indicator ${apiStatus.tone}`}
              aria-hidden="true"
            />
            <span className="eyebrow">Platform connection</span>
            <h2>{apiStatus.title}</h2>
            <p>{apiStatus.detail}</p>
            {apiStatus.phase === 'unavailable' && (
              <button className="primary-action" type="button" onClick={retryApi}>
                Retry connection
              </button>
            )}
          </div>
        </div>
      )}

      <aside className="sidebar">
        <div className="platform-brand">
          <div className="platform-mark">W</div>
          <div>
            <strong>Warren Digital</strong>
            <span>Platform Admin</span>
          </div>
        </div>

        <div className="site-switcher">
          <label htmlFor="site-select">Current site</label>
          <div className="site-select-wrap">
            <span className="site-mark" aria-hidden="true">{site.mark}</span>
            <select
              id="site-select"
              value={siteKey}
              onChange={(event) => switchSite(event.target.value as SiteKey)}
            >
              <option value="warren">Warren Academy</option>
              <option value="turtles">Turtles</option>
            </select>
          </div>
          <span className="site-domain">{site.domain}</span>
        </div>

        <nav className="sidebar-nav" aria-label="Administration navigation">
          <button
            className={activeItem === 'Dashboard' ? 'nav-item active' : 'nav-item'}
            onClick={() => setActiveItem('Dashboard')}
            type="button"
          >
            <span className="nav-dot" aria-hidden="true" />
            Dashboard
          </button>

          {navGroups.map((group) => (
            <div className="nav-group" key={group.label}>
              <p>{group.label}</p>
              {group.items.map((item) => (
                <button
                  className={activeItem === item.label ? 'nav-item active' : 'nav-item'}
                  key={item.label}
                  onClick={() => setActiveItem(item.label)}
                  type="button"
                >
                  <span>{item.label}</span>
                  {item.badge && <small>{item.badge}</small>}
                </button>
              ))}
            </div>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="user-avatar">HD</div>
          <div>
            <strong>Platform administrator</strong>
            <span>All sites</span>
          </div>
          <button type="button" aria-label="Open account menu">•••</button>
        </div>
      </aside>

      <main className="workspace">
        <header className="topbar">
          <div>
            <p className="breadcrumb">{site.name} / {activeItem}</p>
            <h1>{activeItem}</h1>
          </div>
          <div className="topbar-actions">
            <button className="icon-button" type="button" aria-label="Search">⌕</button>
            <button className="icon-button" type="button" aria-label="Notifications">◌</button>
            {!isReadOnlyReview && (
              <button className="primary-action" type="button">+ Create</button>
            )}
          </div>
        </header>

        <section className="workspace-content" aria-live="polite">
          {activeItem === 'Pages' ? (
            <PagesReview siteKey={siteKey} />
          ) : siteKey === 'warren' && activeItem === 'Mandatory Disclosure' ? (
            <MandatoryDisclosureReview siteKey={siteKey} />
          ) : (
            <>
              <div className="welcome-panel">
                <div>
                  <span className="eyebrow">Shared platform · Independent brand</span>
                  <h2>{site.name} content control centre</h2>
                  <p>
                    Manage site-scoped content, media and publishing without exposing or changing the other brand.
                    The shell is ready for API-backed workflows as Phase 1 administration features are implemented.
                  </p>
                </div>
                <div className="brand-preview">
                  <span className="preview-mark">{site.mark}</span>
                  <div>
                    <small>Rendering to</small>
                    <strong>{site.domain}</strong>
                  </div>
                </div>
              </div>

              <div className="metric-grid">
                {metrics.map((metric) => (
                  <article className="metric-card" key={metric.label}>
                    <span>{metric.label}</span>
                    <strong>{metric.value}</strong>
                    <small>{metric.detail}</small>
                  </article>
                ))}
              </div>

              <div className="dashboard-grid">
                <section className="panel recent-panel">
                  <div className="panel-heading">
                    <div>
                      <span className="eyebrow">Editorial activity</span>
                      <h3>Recent content</h3>
                    </div>
                    <button type="button">View all</button>
                  </div>

                  <div className="content-list">
                    {recentItems.map((item) => (
                      <button className="content-row" type="button" key={`${item.type}-${item.title}`}>
                        <span className="content-type">{item.type.slice(0, 1)}</span>
                        <span className="content-main">
                          <strong>{item.title}</strong>
                          <small>{item.type} · {item.time}</small>
                        </span>
                        <span className={`status status-${item.status.toLowerCase()}`}>{item.status}</span>
                        <span aria-hidden="true">→</span>
                      </button>
                    ))}
                  </div>
                </section>

                <aside className="panel publishing-panel">
                  <div className="panel-heading">
                    <div>
                      <span className="eyebrow">Publishing</span>
                      <h3>Site health</h3>
                    </div>
                  </div>

                  <div className="health-list">
                    <div>
                      <span className={`health-dot ${apiStatus.tone}`} aria-hidden="true" />
                      <div><strong>Public API</strong><small>{apiStatus.detail}</small></div>
                      <b>{apiStatus.badge}</b>
                    </div>
                    <div>
                      <span className="health-dot healthy" aria-hidden="true" />
                      <div><strong>Media pipeline</strong><small>Ready</small></div>
                      <b>OK</b>
                    </div>
                    <div>
                      <span className="health-dot healthy" aria-hidden="true" />
                      <div><strong>Static build</strong><small>Latest validation</small></div>
                      <b>PASS</b>
                    </div>
                  </div>

                  <div className="publish-card">
                    <span>Next scheduled publish</span>
                    <strong>Tomorrow · 08:00</strong>
                    <p>Site-specific publishing will remain isolated by brand and domain.</p>
                  </div>
                </aside>
              </div>
            </>
          )}
        </section>
      </main>
    </div>
  );
}

export default App;
